export const SERVICE_FEE_PERCENT = 0.10;

export type PriceInput = number | string | null | undefined;

function toNumber(value: PriceInput): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function isPositive(value: PriceInput): boolean {
  return toNumber(value) > 0;
}

/**
 * Calculate the cheapest rental price for a given number of days using
 * daily, weekly, and monthly rates. If a longer-term rate is cheaper for
 * the requested duration, it is used; otherwise the daily rate is used.
 */
export function calculateRentalPrice(
  dailyPrice: PriceInput,
  weeklyPrice: PriceInput,
  monthlyPrice: PriceInput,
  days: number,
): number {
  if (!Number.isFinite(days) || days < 0) return 0;

  const daily = toNumber(dailyPrice);
  const weekly = isPositive(weeklyPrice) ? toNumber(weeklyPrice) : null;
  const monthly = isPositive(monthlyPrice) ? toNumber(monthlyPrice) : null;

  let bestPrice = daily * days;

  if (weekly && days >= 7) {
    const weeks = Math.floor(days / 7);
    const remainderDays = days % 7;
    const weeklyTotal = weeks * weekly + remainderDays * daily;
    if (weeklyTotal < bestPrice) {
      bestPrice = weeklyTotal;
    }
  }

  if (monthly && days >= 30) {
    const months = Math.floor(days / 30);
    const remainderDays = days % 30;
    const monthlyTotal = months * monthly + remainderDays * daily;
    if (monthlyTotal < bestPrice) {
      bestPrice = monthlyTotal;
    }

    const remainderAfterMonths = days % 30;
    const extraWeeks = Math.floor(remainderAfterMonths / 7);
    const extraDays = remainderAfterMonths % 7;
    const mixedTotal = months * monthly + extraWeeks * (weekly || 0) + extraDays * daily;
    if (mixedTotal < bestPrice) {
      bestPrice = mixedTotal;
    }
  }

  return bestPrice;
}

export function calculateServiceFee(itemPrice: PriceInput): number {
  return Math.round(toNumber(itemPrice) * SERVICE_FEE_PERCENT * 100) / 100;
}

export function calculateTotalAmount(
  itemPrice: PriceInput,
  serviceFee: PriceInput,
  depositAmount: PriceInput,
  shippingCost: PriceInput = null,
): number {
  return toNumber(itemPrice) + toNumber(serviceFee) + toNumber(depositAmount) + toNumber(shippingCost);
}
